import { differenceInMinutes } from 'date-fns';

import type { RedirectPath } from '../common/types/Storage';
import { captureMessage, captureWarning } from '../logger/Logger';
import { stytchClient } from './providers/AuthProvider';

export const getSessionJwt = (): string | null =>
    stytchClient.session.getTokens()?.session_jwt ?? null;

/**
 * Unlike {@link getSessionJwt}, this stays true while the session is alive but
 * the shorter-lived JWT has lapsed.
 */
export const hasActiveStytchSession = (): boolean =>
    stytchClient.session.getSync() != null;

/**
 * Set synchronously at the start of logout so an in-flight admin-user fetch
 * cannot write the signed-in user back before `session.revoke()` returns.
 */
export const isLoggingOutRef = { current: false };

export const getAuthorizationHeader = (): { Authorization: string } => {
    const token = getSessionJwt();
    return {
        Authorization: token != null ? `Bearer ${token}` : '',
    };
};

const ROUTE_PATHS_EXCLUDED_FROM_STORED_REDIRECT = [
    /^\/?$/, // home
    /^\/sign-in\/?$/, // sign-in
];

export const getStoredRedirectPath = (
    staleThresholdMinutes: number = 30,
): string | undefined => {
    let redirectPath: RedirectPath | null = null;
    const raw = localStorage.getItem('REDIRECT_PATH');
    if (raw != null) {
        try {
            redirectPath = JSON.parse(raw) as RedirectPath;
        } catch (err) {
            captureWarning('Invalid JSON found in REDIRECT_PATH storage', err);

            // Clear the invalid json value.
            localStorage.removeItem('REDIRECT_PATH');
        }
    }

    if (redirectPath?.pathname == null) {
        return undefined;
    }

    const shouldIgnorePath = ROUTE_PATHS_EXCLUDED_FROM_STORED_REDIRECT.some(
        (regexp) => regexp.test(redirectPath.pathname),
    );

    const isStale =
        differenceInMinutes(
            new Date(),
            redirectPath.storageTimestamp
                ? new Date(redirectPath.storageTimestamp)
                : new Date(),
        ) > staleThresholdMinutes;

    if (shouldIgnorePath || isStale) {
        clearRedirectPath();
        return undefined;
    }

    return redirectPath.pathname;
};

export const setStoredRedirectPath = (pathname: string) => {
    const shouldIgnoreCurrentPath =
        ROUTE_PATHS_EXCLUDED_FROM_STORED_REDIRECT.some((regexp) =>
            regexp.test(pathname),
        );

    const existingRedirectPath = getStoredRedirectPath();
    const hasExistingRedirectPath = existingRedirectPath !== undefined;

    if (shouldIgnoreCurrentPath || hasExistingRedirectPath) {
        if (
            pathname !== '/' &&
            hasExistingRedirectPath &&
            pathname !== existingRedirectPath
        ) {
            captureMessage(
                `Unexpected attempt to write to stored redirect path because a redirect path already exists; current path: ${pathname}, existing redirect path: ${existingRedirectPath}`,
            );
        }
        return false;
    } else {
        captureMessage(`Setting stored redirect path. pathname: ${pathname}`);
        localStorage.setItem(
            'REDIRECT_PATH',
            JSON.stringify({
                pathname,
                storageTimestamp: new Date().toISOString(),
            }),
        );
        return true;
    }
};

export const clearRedirectPath = () => {
    localStorage.removeItem('REDIRECT_PATH');
};
